/**
 * Utility functions for Employee ID formatting
 */

/**
 * Converts employee ID from string to integer for display purposes
 * Removes leading zeros and returns the numeric value
 * @param empId - Employee ID as string (e.g., "0000859")
 * @returns Employee ID as string with leading zeros removed (e.g., "859") or the original value if conversion fails
 */
export const formatEmpIdForDisplay = (empId: string | number | null | undefined): string => {
  if (empId === null || empId === undefined || empId === '') {
    return '';
  }
  
  // If it's already a number, convert to string
  if (typeof empId === 'number') {
    return empId.toString();
  }
  
  // Convert string to number to remove leading zeros, then back to string
  const stringEmpId = empId.toString().trim();
  
  // Check if it's a valid numeric string
  if (/^\d+$/.test(stringEmpId)) {
    const parsedId = parseInt(stringEmpId, 10);
    return isNaN(parsedId) ? stringEmpId : parsedId.toString();
  }
  
  // If it's not purely numeric, return as is
  return stringEmpId;
};

/**
 * Converts employee ID for display with fallback text
 * @param empId - Employee ID as string
 * @param fallback - Fallback text (default: "N/A")
 * @returns Formatted employee ID or fallback text
 */
export const formatEmpIdWithFallback = (empId: string | number | null | undefined, fallback: string = "N/A"): string => {
  if (empId === null || empId === undefined || empId === '') {
    return fallback;
  }
  
  const formatted = formatEmpIdForDisplay(empId);
  return formatted || fallback;
};

/**
 * Formats employee ID for search and API payload.
 * If the input is numeric and length <= 8, pads leading zeros to 8 digits total.
 * E.g., "859" -> "00000859"
 */
export const formatEmployeeId = (input: string | number | null | undefined): string => {
  if (input === null || input === undefined || input === '') {
    return '';
  }
  const str = input.toString().trim();
  if (/^\d+$/.test(str) && str.length <= 8) {
    return str.padStart(8, '0');
  }
  return str;
};

